import * as R from 'ramda'
import { useCallback } from 'react'
import {
  MarkChatReadOutlined,
  MarkChatUnreadOutlined,
} from '@mui/icons-material'
import { LIMITS } from '@weblite/permission'
// Common
import { PopupMenuItem } from '$common/popup-menu'
import { PinIcon } from '$common/icons'
import { BlockIcon } from '$common/icons/block'
import { ClearHistoryIcon } from '$common/icons/clear-history'
// Logic
import {
  RoomListItem,
  WorkspaceType,
} from '$logic/room-list/room-list.react-hook'
import { getRoom, markRoomAsRead, markRoomAsUnread } from '$logic/room'
import { getRoomUnseenCountAndUnreadMark } from '$logic/room-seen'
import { useAsyncFunction } from '$func/async-function.react-hook'
// Localization
import { getPinnedRooms, pinRooms, unPinRooms } from '$logic/user-preferences'
import { blockUser } from '$logic/user-policy'
import { getUserIdsByPvIds } from '$logic/pv'
import { getUser } from '$logic/users'
import { t } from '$i18n'
import { showClearAllPVsHistory } from '$comp/modals/confirmation-dialog'
import { showPinRoomLimitSnackbar } from '$comp/snackbars'
import { ContextMenuList } from '$comp/_app/components/chat/components/body/components/context-menu/components'
import { DiscussionOutlinedIcon } from '@weblite/icons'
import { PlusIcon } from '$common/icons/plus'
import { showSelectWorkspace } from '$comp/modals/select-workspace'
import {
  showAddHallToWorkspaceConfirmModal,
  showRemoveHallFromWorkspaceConfirmModal,
} from '$comp/modals/confirmation-dialog/confirmation-dialog.api'
import { isClientUserAdminOfHall } from '$logic/hall'
import { RoomTypes } from '@weblite/ts-action/room'

// TODO: Implement logic later
async function getRoomListMenuUsers(roomIds: string[]) {
  const rooms = await Promise.all(roomIds.map(getRoom))
  return R.all(R.propEq('type', 'PV'), rooms)
}

interface Props {
  chatItems: RoomListItem[]
  onClose: () => unknown
  xAxis: number
  yAxis: number
}

function canRoomBeInWorkspace(room: RoomListItem) {
  return room.type === 'CHANNEL' || room.type === 'GROUP'
}

export function ChatListMenu({ chatItems, onClose, yAxis, xAxis }: Props) {
  const canPinRooms = R.reject(R.prop('pinned'), chatItems)
  const canUnPinRooms = R.filter(R.prop('pinned'), chatItems)

  const areAllRoomsWorkspaces = chatItems.every(
    ({ type }) => type === WorkspaceType,
  )

  const areAllRoomsNonWorkspaces = chatItems.every(
    ({ type }) => type !== WorkspaceType,
  )

  const areAllRoomsSameType = areAllRoomsWorkspaces || areAllRoomsNonWorkspaces

  const { value: isAdminOfHallValues, loading: loadingIsAdmin } =
    useAsyncFunction(
      () =>
        Promise.all(
          chatItems.map(({ chatId }) => isClientUserAdminOfHall(chatId)),
        ),
      [chatItems],
    )

  const isAdminOfAllHalls =
    isAdminOfHallValues?.every(Boolean) && !loadingIsAdmin

  const canAddHallToWorkspace =
    chatItems.every(item => canRoomBeInWorkspace(item) && !item.workspaceId) &&
    isAdminOfAllHalls

  const canRemoveHallFromWorkspace =
    chatItems.every(item => canRoomBeInWorkspace(item) && !!item.workspaceId) &&
    isAdminOfAllHalls

  const canPin =
    areAllRoomsSameType &&
    !!(canPinRooms.length && canPinRooms.length < LIMITS.pinnedRoomsCount)

  const canUnPin = areAllRoomsSameType && !!canUnPinRooms.length

  const { loading, value: roomsUnseenCountAndUnreadMark } = useAsyncFunction(
    () =>
      areAllRoomsNonWorkspaces
        ? Promise.all(
            chatItems.map(({ chatId }) =>
              getRoomUnseenCountAndUnreadMark(chatId),
            ),
          )
        : Promise.resolve([]),
    [chatItems, areAllRoomsNonWorkspaces],
  )

  const { value: areAllUsers } = useAsyncFunction(
    () =>
      areAllRoomsNonWorkspaces
        ? getRoomListMenuUsers(chatItems.map(R.prop('chatId')))
        : Promise.resolve(false),
    [chatItems, areAllRoomsNonWorkspaces],
  )

  const canMarkAsRead =
    areAllRoomsNonWorkspaces &&
    !loading &&
    R.any(
      ({ unseenCount, unreadMark }) => Boolean(unseenCount || unreadMark),
      roomsUnseenCountAndUnreadMark,
    )

  const canMarkAsUnread = areAllRoomsNonWorkspaces && !canMarkAsRead && !loading

  const onPin = async () => {
    const pinnedRooms = await getPinnedRooms()
    if (pinnedRooms.length + canPinRooms.length <= LIMITS.pinnedRoomsCount) {
      analite('CHATLIST_PIN_CHAT', { active: true })
      await pinRooms(canPinRooms.map(R.prop('chatId')))
    } else showPinRoomLimitSnackbar()
    onClose()
  }

  const onUnPin = async () => {
    analite('CHATLIST_PIN_CHAT', { active: false })
    await unPinRooms(canUnPinRooms.map(R.prop('chatId')))
    onClose()
  }

  const onBlock = async () => {
    const selectedChatsUserIds = await getUserIdsByPvIds(
      R.pluck('chatId', chatItems),
    )
    const selectedUsersList = await Promise.all(
      selectedChatsUserIds.map(getUser),
    )
    await Promise.all(
      selectedUsersList
        .filter(user => !user.isBlocked)
        .map(({ id }) => blockUser(id)),
    )
    analite('BLOCK_USER', {
      active: true,
      source: 'CHAT_LIST',
    })
    onClose()
  }

  const onClearHistory = async () => {
    await showClearAllPVsHistory(R.pluck('chatId', chatItems))
    onClose()
  }

  const onMarkAsRead = useCallback(async () => {
    await Promise.all(chatItems.map(({ chatId }) => markRoomAsRead(chatId)))
    await onClose()
  }, [chatItems])

  const onMarkAsUnread = useCallback(async () => {
    await Promise.all(chatItems.map(({ chatId }) => markRoomAsUnread(chatId)))
    await onClose()
  }, [chatItems])

  const onAddHallToWorkspace = useCallback(async () => {
    await showSelectWorkspace(
      async (workspaceId: string, workspaceName: string) => {
        showAddHallToWorkspaceConfirmModal(
          workspaceId,
          workspaceName,
          chatItems.map(R.prop('chatId')),
        )
      },
    )
    await onClose()
  }, [chatItems])

  const onRemoveHallFromWorkspace = useCallback(async () => {
    showRemoveHallFromWorkspaceConfirmModal(chatItems.map(R.prop('chatId')))
    await onClose()
  }, [chatItems])

  return (
    <ContextMenuList onClose={onClose} open xAxis={xAxis} yAxis={yAxis}>
      {canPin && (
        <PopupMenuItem text={t('chatList.menu.pin')} onClick={onPin}>
          <PinIcon sx={{ color: 'secondary.main' }} />
        </PopupMenuItem>
      )}

      {canUnPin && !canPin && (
        <PopupMenuItem text={t('chatList.menu.unPin')} onClick={onUnPin}>
          <PinIcon sx={{ color: 'secondary.main' }} />
        </PopupMenuItem>
      )}

      {areAllUsers && (
        <PopupMenuItem text={t('profile.user.block')} onClick={onBlock}>
          <BlockIcon sx={{ color: 'secondary.main' }} />
        </PopupMenuItem>
      )}

      {areAllUsers && (
        <PopupMenuItem
          text={t('menu.common.clearHistory')}
          onClick={onClearHistory}
        >
          <ClearHistoryIcon />
        </PopupMenuItem>
      )}

      {canMarkAsRead && (
        <PopupMenuItem
          text={t('menu.common.markAsRead')}
          onClick={onMarkAsRead}
        >
          <MarkChatReadOutlined
            viewBox="0 0 24 24"
            sx={{ color: 'secondary.main' }}
          />
        </PopupMenuItem>
      )}

      {canMarkAsUnread && (
        <PopupMenuItem
          text={t('menu.common.markAsUnread')}
          onClick={onMarkAsUnread}
        >
          <MarkChatUnreadOutlined sx={{ color: 'secondary.main' }} />
        </PopupMenuItem>
      )}

      {canAddHallToWorkspace && (
        <PopupMenuItem
          text={t('chatList.menu.addHallToWorkspace')}
          onClick={onAddHallToWorkspace}
        >
          <PlusIcon sx={{ color: 'secondary.main' }} />
        </PopupMenuItem>
      )}

      {canRemoveHallFromWorkspace && (
        <PopupMenuItem
          text={t('chatList.menu.removeHallFromWorkspace')}
          onClick={onRemoveHallFromWorkspace}
        >
          <DiscussionOutlinedIcon sx={{ color: 'secondary.main' }} />
        </PopupMenuItem>
      )}
    </ContextMenuList>
  )
}
